import * as THREE from 'three';

/* ═══════════════════════════════════════════════════════════════════════════
   La CUISSON du flipbook d'explosion (PLAN.md §4.7, couche 1).

   Le plan est catégorique : le volume turbulent d'une explosion est ce que les
   particules simulent le plus mal et le plus cher — une planche pré-rendue
   échantillonnée sur un quad donne un résultat cinématographique pour trois
   draw calls et zéro simulation. Le hic, documenté comme « chemin critique n°1
   du contenu », est que ces planches N'EXISTENT PAS en CC0 : il faudrait
   EmberGen, des jours de Blender Mantaflow, ou l'achat d'un pack.

   La sortie retenue est la même que pour les icônes PWA et les silhouettes de
   vaisseau : on GÉNÈRE l'asset, on ne le source pas. On rend, UNE FOIS au boot,
   une boule de feu procédurale (fbm turbulent qui billonne et se dissipe) dans
   une grille N×N sur une texture. Ensuite le cœur d'explosion n'échantillonne
   qu'une planche — exactement l'architecture que le plan décrit, mais sans
   dépendance externe et avec une VRAM mesurable.

   Ce qu'on assume honnêtement : ce n'est pas un rendu volumétrique EmberGen. Le
   choix reste réversible — remplacer la planche par un vrai rendu ne toucherait
   que ce fichier, `core()` continuant de l'échantillonner sans rien savoir de sa
   provenance.

   Aucune consommation d'aléatoire de simulation : la graine est un simple
   uniforme du shader de cuisson. Rien de tout cela n'influence un tick.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Une planche cuite : sa texture échantillonnable et sa géométrie de grille. */
export interface Flipbook {
  readonly texture: THREE.Texture;
  /** Côté de la grille (N pour une planche N×N). */
  readonly grid: number;
  /** Nombre d'images utiles (grid²). */
  readonly frames: number;
  /** Octets de VRAM occupés — pour la documentation du budget (jalon J5). */
  readonly vramBytes: number;
  /** Libère la cible de rendu. */
  dispose(): void;
}

export interface BakeOptions {
  /** Côté de la grille. 8 pour les explosions courantes, 4 pour un boss (images
   *  plus grosses, donc plus nettes une fois étirées plein écran). */
  readonly grid: number;
  /** Côté d'une image, en pixels. La texture fait donc `grid × frameSize`. */
  readonly frameSize: number;
  /** Graine du bruit — deux planches de graines différentes ne se ressemblent
   *  pas, ce qui évite l'impression de « toujours la même explosion ». */
  readonly seed?: number;
}

const BAKE_FRAG = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform float uGrid;
uniform float uSeed;

// ── Bruit de valeur 3D + fbm. La 3ᵉ dimension est le TEMPS de vie de l'image :
//    des images voisines partagent donc un bruit cohérent, et la boule billonne
//    de façon continue au lieu de scintiller d'une image à l'autre.
float hash(vec3 p) {
  p = fract(p * 0.3183099 + 0.1);
  p *= 17.0;
  return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
}
float vnoise(vec3 x) {
  vec3 i = floor(x);
  vec3 f = fract(x);
  f = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(mix(hash(i + vec3(0, 0, 0)), hash(i + vec3(1, 0, 0)), f.x),
        mix(hash(i + vec3(0, 1, 0)), hash(i + vec3(1, 1, 0)), f.x), f.y),
    mix(mix(hash(i + vec3(0, 0, 1)), hash(i + vec3(1, 0, 1)), f.x),
        mix(hash(i + vec3(0, 1, 1)), hash(i + vec3(1, 1, 1)), f.x), f.y),
    f.z);
}
float fbm(vec3 p) {
  float a = 0.5, s = 0.0;
  for (int o = 0; o < 4; o++) {
    s += a * vnoise(p);
    p *= 2.02;
    a *= 0.5;
  }
  return s;
}

void main() {
  // Cellule (image) et coordonnée LOCALE dans la cellule.
  vec2 cell = floor(vUv * uGrid);
  vec2 local = fract(vUv * uGrid);

  // Vie normalisée de l'image, 0 (naissance) → 1 (dissipée).
  float frame = cell.y * uGrid + cell.x;
  float f = frame / (uGrid * uGrid - 1.0);

  vec2 p = local * 2.0 - 1.0; // -1..1, centré
  float rad = length(p);

  // La boule GONFLE : rayon franc au départ (racine = attaque nette), puis
  // large. On érode ensuite les bords par le bruit, de plus en plus avec l'âge.
  float R = mix(0.16, 1.02, sqrt(f));

  vec3 np = vec3(p * 2.1, f * 1.6 + uSeed);
  float warp = fbm(np);
  float n = fbm(np * 1.7 + warp * 0.7);

  float body = smoothstep(R, R * 0.28, rad); // 1 au cœur, 0 au-delà de R
  float erode = mix(0.05, 1.35, f);
  float density = clamp(body * (0.55 + 0.85 * n) - erode * n * rad, 0.0, 1.0);

  // Fondu de fin de vie et coupure franche à l'extérieur du disque.
  density *= smoothstep(1.02, 0.72, f);
  density *= step(rad, 1.05);

  // Température : plus chaud au cœur et tôt dans la vie. Blanc → jaune → orange,
  // JAMAIS de rouge saturé — il est réservé à ce qui tue (PLAN.md §4.5).
  float temp = clamp((1.0 - rad * 1.05) * (1.0 - f * 0.65) + n * 0.22, 0.0, 1.0);
  vec3 orange = vec3(1.0, 0.45, 0.12);
  vec3 yellow = vec3(1.0, 0.82, 0.30);
  vec3 white = vec3(1.0, 0.98, 0.92);
  vec3 col = mix(orange, yellow, smoothstep(0.18, 0.55, temp));
  col = mix(col, white, smoothstep(0.62, 0.95, temp));

  // Éclat de la première frame : c'est lui qui déclenchera le bloom.
  col *= 1.0 + (1.0 - f) * (1.0 - f) * 1.4;

  gl_FragColor = vec4(col, density);
}
`;

const BAKE_VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

/**
 * Cuit une planche d'explosion et renvoie sa texture. Rendu UNE FOIS, au boot :
 * après, plus rien ne remonte au GPU pour le cœur — on ne fait qu'échantillonner.
 *
 * ⚠️ Restaure la cible de rendu à `null` en sortant : appelée pendant l'amorçage,
 * avant la boucle, elle ne doit pas laisser le renderer pointer sur sa cible.
 */
export function bakeExplosionSheet(renderer: THREE.WebGLRenderer, opts: BakeOptions): Flipbook {
  const { grid, frameSize } = opts;
  const size = grid * frameSize;

  const target = new THREE.WebGLRenderTarget(size, size, {
    depthBuffer: false,
    stencilBuffer: false,
    format: THREE.RGBAFormat,
    type: THREE.UnsignedByteType,
    // Bilinéaire : l'échantillonnage inter-image du cœur veut une texture lisse.
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
  });
  target.texture.generateMipmaps = false;
  // La coupure `step(rad,1.05)` laisse les bords des cellules à zéro : aucun
  // débordement bilinéaire d'une image sur sa voisine (du noir déborde du noir).

  const scene = new THREE.Scene();
  const cam = new THREE.Camera();
  const mat = new THREE.ShaderMaterial({
    vertexShader: BAKE_VERT,
    fragmentShader: BAKE_FRAG,
    uniforms: { uGrid: { value: grid }, uSeed: { value: opts.seed ?? 0 } },
    depthTest: false,
    depthWrite: false,
  });
  // Catalogue de matériaux fermé (§4.8) : tout shader injecté déclare sa clé,
  // même celui-ci, éphémère. Le programme est créé au boot puis disposé — la clé
  // le rend identifiable si jamais il traînait dans le compte du warm-up.
  mat.customProgramCacheKey = (): string => 'explosion-bake-v1';
  const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
  quad.frustumCulled = false;
  scene.add(quad);

  const prevTarget = renderer.getRenderTarget();
  renderer.setRenderTarget(target);
  renderer.render(scene, cam);
  renderer.setRenderTarget(prevTarget);

  // La géométrie et le matériau de cuisson ne resservent jamais.
  quad.geometry.dispose();
  mat.dispose();

  return {
    texture: target.texture,
    grid,
    frames: grid * grid,
    vramBytes: size * size * 4,
    dispose: () => target.dispose(),
  };
}
